import { TFunction } from 'i18next';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { keyValueForEnum } from 'src/js/helper/enum-helper';
import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { IconStatus } from 'src/js/model/icon-status-type';
import { PRODUCT, constant } from 'src/js/constant';
import * as yup from 'yup';

export interface VideoEncoder {
  id: number;
  name: string;
  interface: number;
  aspectRatio: ASPECT_RATIO;
  bitrate: number;
  chromaSubSampling: CHROMA_SUBSAMPLING;
  closedCaption: boolean;
  codecAlgorithm: VIDEO_CODEC;
  colorSpace?: string;
  colorSpaceActual?: string; // only printed when differing
  encodingProfile?: ENCODING_PROFILE;
  preferredResolution?: string;
  copyProtectedContent?: boolean; // DVI only
  countingMode: COUNTING_MODE;
  cropping: RESIZE_MODE;
  dailyResync: boolean;
  dynamicRangeMode?: DYNAMIC_RANGE_MODE; // hasHDR only
  gopSize: number;
  gopSizeAdapted?: number;
  gopStructure: GOP_STRUCTURE;
  hdcp?: boolean;
  hdcpAuthenticated?: boolean;
  hdcpEnabledAtBoot?: boolean;
  hdcpEncrypted?: boolean;
  inputInterface?: VIDIN_CONNECTOR; // VideoInput, FX only
  intraRefresh: boolean;
  maxBitrate: number | string;
  pictureRate: number;
  partialFrameSkip: boolean;
  dynamicPartialImageSkip: boolean;
  previewEnabled?: boolean;
  previewIntervalSec?: number;
  rateControl: RATE_CONTROL;
  rateControlAdapted?: RATE_CONTROL;
  resolution: string; // UINT64 as string
  resolutionText: string;
  resyncHour: number;
  sceneChangeDetection?: boolean;
  sceneChangeThreshold?: number;
  slices: number;
  timeCode: TIMECODE_SOURCE;

  // video input related
  firFilter: boolean;
  videoInputState?: string;
}

export interface VideoEncoderStats {
  aspectRatio: string;
  closedCaptioning: boolean;
  droppedFrames: string;
  encodedBytes: string;
  encodedBitRate: string;
  encodedFrameRate: number;
  encodedFrames: string;
  encoderLoad: number;
  encoderPTS: string;
  encoderResets: number;
  h264Profile?: string;
  h264Level?: string;
  h265Level?: string;
  h265Profile?: string;
  h265Tier?: string;
  extractedCCBytes?: string;
  CCErrors?: string;
  timecodeSource?: string;
  timecode?: string;
  sourceSwitches?: string;
  UTC?: string;
  UTCFormatted?: string /* frontend construct */;
  inputColorPrimaries: string;
  inputFormat: string;
  inputPresent: boolean;
  inputFormatHeight?: number;
  inputFormatIsDetailed?: boolean;
  inputFormatShort?: string;
  inputFormatFrameRate?: number;
  inputFormatIsInterlaced?: boolean;
  inputFormatIsProgressive?: boolean;
  inputFormatResolution?: string;
  inputFormatU64?: string;
  inputFormatWidth?: number;
  inputFormatWithoutFramerateU64?: string;
  inputMatrixCoefficients?: string;
  inputTransferCharacteristics?: string;
  inputType?: number;
  inputTypeString?: string;
  numberOfSlices: string;
  resolution: string;
  resolutionFrameRate: number;
  resolutionHeight: number;
  resolutionIsDetailed: boolean;
  resolutionIsInterlaced: boolean;
  resolutionIsProgressive: boolean;
  resolutionWidth: number;
  scalerLoad: number;
  signaledDynamicRange?: string;
  state: VideoEncoderState;
  uptime: string;
  sceneChangesDetected?: string;
  lastSceneChange?: string;
  lastReset: string;
}

export interface VideoEncoderResponse extends GenericResponse {
  info: VideoEncoder;
  stats: VideoEncoderStats;
}

export interface GetAllVideoEncodersResponse {
  data: VideoEncoderResponse[];
}

export interface VideoEncoderViewModel extends VideoEncoder {
  stats: VideoEncoderStats;
  state: VideoEncoderState;

  bitrate: number;
  stateSortable: number;
  interfaceTxt: string;
  codecTxt: string;
  codecTxtLong: string;

  gopSize: number;
  gopSizeAdapted?: number;
  stateString: string;
  dashboardColor: string;
  inputFormat: string;
  outputResolution: string;
  maxBitrate: number | string;

  status: string;
  selected: boolean /* checked by checkbox */;
  hidden: boolean /* when filtered out */;
  updated: string /* to trigger setState to detect change and re-render */;
}

export interface ResolutionItem {
  name: string;
  value: string;
  frameRates?: number[];
}

export interface ResolutionsMatrix {
  [key: string]: Array<ResolutionItem>;
}

export enum VIDIN_CONNECTOR {
  AUTO_DETECT = -1,
  UNKNOWN,
  SDI,
  COMPOSITE,
  HDMI,
  DISPLAYPORT,
}

export enum VIDEO_CODEC {
  H264 = 0,
  H265 = 1,
}

export enum ENCODING_PROFILE {
  BASELINE = 1,
  MAIN = 2,
  HIGH = 3,
  HIGH_10 = 4,
  HIGH_422 = 5,
  MAIN_10 = 6,
  MAIN_422 = 7,
}

export enum ENCODING_PROFILE_H264 {
  BASELINE = 1,
  MAIN = 2,
  HIGH = 3,
  HIGH_10 = 4,
  HIGH_422 = 5,
}
export enum ENCODING_PROFILE_HEVC {
  MAIN = 2,
  MAIN_10 = 6,
  MAIN_422 = 7,
}

export enum CHROMA_SUBSAMPLING {
  CHROMA_420_8BIT = 1,
  CHROMA_420_10BIT = 2,
  CHROMA_422_8BIT = 3,
  CHROMA_422_10BIT = 4,
}

export enum COUNTING_MODE {
  SMPTE_12M_1 = 1,
  UTC_CONVERSION = 0,
}

export enum DYNAMIC_RANGE_MODE {
  OFF = 0,
  AUTOMATIC = 1,
  HDR_HLG = 2,
  HDR_PQ = 3,
}

export enum DYNAMIC_RANGE_MODE_8BIT {
  OFF = 0,
  AUTOMATIC = 1,
}

export enum RATE_CONTROL {
  CBR = 0,
  CVBR = 1,
}

export enum ASPECT_RATIO {
  AUTOMATIC = 0,
  ASPECT_RATIO_1_2 = 20,
  ASPECT_RATIO_3_2 = 13,
  ASPECT_RATIO_4_3 = 2,
  ASPECT_RATIO_3_3 = 3,
  ASPECT_RATIO_5_4 = 4,
  ASPECT_RATIO_16_9 = 5,
  ASPECT_RATIO_16_10 = 14,
  ASPECT_RATIO_17_9 = 15,
  ASPECT_RATIO_WSS_AFD = 1,
}

export enum ASPECT_RATIO_WITHOUT_WSS {
  AUTOMATIC = 0,
  ASPECT_RATIO_1_2 = 20,
  ASPECT_RATIO_3_2 = 13,
  ASPECT_RATIO_4_3 = 2,
  ASPECT_RATIO_3_3 = 3,
  ASPECT_RATIO_5_4 = 4,
  ASPECT_RATIO_16_9 = 5,
  ASPECT_RATIO_16_10 = 14,
  ASPECT_RATIO_17_9 = 15,
}

export enum RESIZE_MODE /* Possible values for video encoder config's enResizeMode field */ {
  SCALE = 0,
  CROP,
}

export enum GOP_STRUCTURE {
  I = 1,
  IP = 0,
  IBP = 2,
  IBBP = 3,
  IBBBP = 4,
  IBBBBP = 5,
}

export enum GOP_STRUCTURE_IP {
  I = 1,
  IP = 0,
}

export enum TIMECODE_SOURCE {
  NONE = 0,
  VIDEO = 1,
  SYSTEM = 2,
}

export enum VideoEncoderState {
  STOPPED = 0,
  AWAIT_FRAMING = (1 << 0) | (1 << 1),
  NOT_ENCODING = (1 << 0) | (1 << 2),
  WORKING = (1 << 0) | (1 << 1) | (1 << 2),
  RESETTING = 1 << 3,
  FAILED = 1 << 7,
}

export const ColorSpaces = [
  'Auto',
  'RGB_8bit_limited',
  'RGB_8Bit_full',
  'RGB_10bit_limited',
  'RGB_10Bit_full',
  'RGB_12bit_limited',
  'RGB_12Bit_full',
  'RGB_16bit_limited',
  'RGB_16Bit_full',
  'YCbCr_422_8bit_limited',
  'YCbCr_422_8Bit_full',
  'YCbCr_422_10bit_limited',
  'YCbCr_422_10bit_full',
  'YCbCr_422_12bit_limited',
  'YCbCr_422_12bit_full',
  'YCbCr_422_16bit_limited',
  'YCbCr_422_16bit_full',
  'YCbCr_444_8bit_limited',
  'YCbCr_444_8bit_full',
  'YCbCr_444_10bit_limited',
  'YCbCr_444_10bit_full',
  'YCbCr_444_12bit_limited',
  'YCbCr_444_12bit_full',
  'YCbCr_444_16bit_limited',
  'YCbCr_444_16bit_full',
];

export const vidEncStateToIcon = (enState: VideoEncoderState): IconStatus => {
  switch (enState) {
    case VideoEncoderState.WORKING:
      return IconStatus.StatusTranscoding;
    case VideoEncoderState.AWAIT_FRAMING:
      return IconStatus.StatusStreamFailed;
    case VideoEncoderState.FAILED:
      return IconStatus.StatusStreamFailed;
    case VideoEncoderState.STOPPED:
      return IconStatus.StatusStopped;
    case VideoEncoderState.RESETTING:
      return IconStatus.StatusWarning;
    case VideoEncoderState.NOT_ENCODING:
      return IconStatus.StatusStreamFailed;
  }
  return IconStatus.StatusStreamFailed;
};

export const vidEncStateToColor = (enState: VideoEncoderState): string | undefined => {
  switch (enState) {
    case VideoEncoderState.WORKING:
      return 'haiui-green-01';
    case VideoEncoderState.STOPPED:
      return 'haiui-gray-07';
    case VideoEncoderState.AWAIT_FRAMING:
    case VideoEncoderState.NOT_ENCODING:
    case VideoEncoderState.RESETTING:
      return 'haiui-amber-01';
    case VideoEncoderState.FAILED:
      return 'haiui-red-01';
  }
  return undefined;
};

export const encoderInterface = (interfaceNumber: number, inputInterface: VIDIN_CONNECTOR) => {
  if (systemInfoCapabilities.is2110()) {
    return (interfaceNumber + 1).toString();
  }

  if (constant.product === PRODUCT.FX) {
    if (inputInterface === VIDIN_CONNECTOR.HDMI) {
      return 'HDMI';
    } else if (inputInterface === VIDIN_CONNECTOR.DISPLAYPORT) {
      return 'DisplayPort';
    } else {
      return 'SDI';
    }
  }

  switch (interfaceNumber) {
    case 0:
      return 'BNC-1';
    case 1:
      return 'BNC-2';
    case 2:
      return 'BNC-3';
    case 3:
      return 'BNC-4';
    default:
      return 'Invalid';
  }
};

const mapDashboardColor = (id: number, state: VideoEncoderState) => {
  if (state !== VideoEncoderState.WORKING) {
    return 'haiui-gray-07';
  }

  if (id === 0) {
    return constant.isMissionCritical ? 'haiui-aqua-01' : 'haiui-purple-01';
  } else if (id === 1) {
    return 'haiui-blue-04';
  } else if (id === 2) {
    return constant.isMissionCritical ? 'haiui-purple-01' : 'haiui-aqua-01';
  } else if (id === 3) {
    return 'haiui-yellow-01';
  } else if (id === 4) {
    return 'haiui-orange-01';
  } else if (id === 5) {
    return 'haiui-pink-01';
  } else if (id === 6) {
    return 'haiui-pistachio-01';
  } else if (id === 7) {
    return 'haiui-coral-01';
  } else {
    return 'haiui-gray-01';
  }
};

const encoderStateToString = (state: VideoEncoderState, t: TFunction): string => {
  const strings = keyValueForEnum(VideoEncoderState);
  const item = strings.find((x) => x[1] === state);
  if (item) {
    return t(`videnc.states.${item[0]}`);
  }
  return t('general.unknown');
};

const encoderStateToSortable = (state: VideoEncoderState): number => {
  switch (state) {
    case VideoEncoderState.WORKING:
      return 1;
    case VideoEncoderState.AWAIT_FRAMING:
    case VideoEncoderState.NOT_ENCODING:
    case VideoEncoderState.RESETTING:
    case VideoEncoderState.FAILED:
      return 2;
    case VideoEncoderState.STOPPED:
      return 3;
    default:
      return 0;
  }
};

// For ActionBar status filter
export const encoderStateToStatus = (state: VideoEncoderState): string => {
  switch (state) {
    case VideoEncoderState.WORKING:
      return 'ENCODING';
    case VideoEncoderState.AWAIT_FRAMING:
    case VideoEncoderState.NOT_ENCODING:
    case VideoEncoderState.RESETTING:
    case VideoEncoderState.FAILED:
      return 'ALERT';
    case VideoEncoderState.STOPPED:
      return 'INACTIVE';
    default:
      return '';
  }
};

export const encoderModelToStatus = (model: VideoEncoderViewModel): string => {
  return encoderStateToStatus(model.state);
};

export const encoderIsStopped = (state: VideoEncoderState): boolean => state === VideoEncoderState.STOPPED;

export const encoderIsStarted = (state: VideoEncoderState): boolean => state !== VideoEncoderState.STOPPED;
// ActionBar helpers ENDs

export interface MkRange {
  min: number;
  max: number;
}

// Will output the resolution without framerate, such as 1920x1080p
// Can be used with either input format or output resolution
export const formatResolution = (resolution: string) => {
  if (resolution != null && !resolution.includes('Input') && typeof resolution.lastIndexOf == 'function') {
    let pos = resolution.lastIndexOf('p');
    if (pos === -1) {
      pos = resolution.lastIndexOf('i');
    }
    if (pos > 0) {
      resolution = resolution.substring(0, pos + 1);
    }
    return resolution;
  }

  return 'Input/Auto';
};

export const formatOutputResolutionWithoutFrameRate = (model: any) => {
  const resolutionStage1 = model.stats && model.stats.inputPresent ? model.stats.resolution : model.resolutionText;

  const resolution = formatResolution(resolutionStage1);
  return resolution;
};

export const formatOutputResolutionWithFrameRate = (model: any) => {
  const resolutionStage1 = model.stats && model.stats.inputPresent ? model.stats.resolution : model.resolutionText;

  let resolution = formatResolution(resolutionStage1);
  let frameRate = 0;
  let interlacedInput = false;
  let progressiveOutput = false;

  frameRate = model.info.pictureRate;

  if (model.stats.inputPresent) {
    let tempRate = model.stats.inputFormatFrameRate;

    interlacedInput = model.stats.inputFormatIsInterlaced;
    progressiveOutput = model.stats.resolutionIsProgressive;

    if (interlacedInput && progressiveOutput) {
      if (tempRate === 29) {
        tempRate = 59;
      } else {
        tempRate *= 2;
      }
    }

    if (frameRate === 0 || tempRate < frameRate) {
      frameRate = tempRate;
    }
  }

  if (resolution !== 'Auto-Detect' && resolution !== 'Input/Auto' && frameRate > 0) {
    resolution += frameRate;
  }
  return resolution;
};

export const isInterlaced = (format: string | number) => {
  if (typeof format === 'string') {
    format = format.substring(format.length - 5); // take just the end. Anyway the interlace flag is the last digit (first bit)
    format = parseInt(format, 10);
  }
  return (format & 1) === 1;
};

export const isProgressive = (format: string | number) => {
  if (typeof format === 'string') {
    format = format.substring(format.length - 5); // take just the end. Anyway the interlace flag is the last digit (first bit)
    format = parseInt(format, 10);
  }
  return (format & 1) === 0;
};

export const mapVideoEncoderViewModel = (response: VideoEncoderResponse, t: TFunction): VideoEncoderViewModel => ({
  ...response.info,
  inputInterface: response.info.inputInterface || VIDIN_CONNECTOR.SDI,
  stats: { ...response.stats, inputPresent: !!response.stats.inputPresent },
  state: response.stats.state,

  firFilter: response.info.firFilter,

  stateSortable: encoderStateToSortable(response.stats.state),
  interfaceTxt: encoderInterface(response.info.interface, response.info.inputInterface),
  codecTxt: response.info.codecAlgorithm === VIDEO_CODEC.H265 ? 'H.265' : 'H.264',
  codecTxtLong: response.info.codecAlgorithm === VIDEO_CODEC.H265 ? 'HEVC/H.265' : 'AVC/H.264',
  stateString: encoderStateToString(response.stats.state, t),
  dashboardColor: mapDashboardColor(response.info.id, response.stats.state),
  inputFormat: response.stats.inputFormatIsDetailed ? response.stats.inputFormatShort : t('videnc.noInput'),
  outputResolution: formatOutputResolutionWithFrameRate(response),
  maxBitrate: Number(response.info.maxBitrate) > 0 ? response.info.maxBitrate : '',
  bitrate: response.info.bitrate,
  gopSize: response.info.gopSize,
  colorSpace: response.info.colorSpace ?? 'Auto',
  preferredResolution: response.info.preferredResolution,

  previewIntervalSec: response.info.previewIntervalSec ?? 10,

  status: encoderStateToStatus(response.stats.state), // required for ActionBar
  selected: false,
  hidden: false,
  updated: new Date().toISOString(),
});

export const mapVideoEncodersResponse = (
  response: GetAllVideoEncodersResponse,
  t: TFunction,
): VideoEncoderViewModel[] => {
  const mapped = response?.data.map((item) => mapVideoEncoderViewModel(item, t));
  return mapped;
};

/*
 * Validation
 */
export const videoEncoderValidationSchema = (t: TFunction) =>
  yup.object().shape({
    name: yup.string().max(63, t('validation.maxLength', { length: 63 })),
    bitrate: yup
      .number()
      .required(t('validation.required'))
      .typeError(t('validation.videoEncoder.bitrate'))
      .min(32, t('validation.videoEncoder.bitrate'))
      .max(120000, t('validation.videoEncoder.bitrate')),
    maxBitrate: yup.mixed().when('rateControl', {
      is: RATE_CONTROL.CVBR,
      then: yup
        .number()
        .typeError(t('validation.videoEncoder.bitrate'))
        .min(32, t('validation.videoEncoder.bitrate'))
        .max(120000, t('validation.videoEncoder.bitrate')),
    }),
    gopSize: yup
      .number()
      .required(t('validation.required'))
      .typeError(t('validation.videoEncoder.gopSize'))
      .min(1, t('validation.videoEncoder.gopSize'))
      .max(1000, t('validation.videoEncoder.gopSize')),
    sceneChangeThreshold: yup.mixed().when('sceneChangeDetection', {
      is: true,
      then: yup
        .number()
        .required(t('validation.required'))
        .typeError(t('validation.videoEncoder.sceneChangeThreshold'))
        .min(1, t('validation.videoEncoder.sceneChangeThreshold'))
        .max(100, t('validation.videoEncoder.sceneChangeThreshold')),
    }),
  });
